'use client';

/**
 * @file PreAuthImportSection
 * @description Drop zone for importing instruments before signup
 * Part of the pre-auth import flow to show value before commitment
 */

import { useState, useRef, useId, useCallback } from 'react';
import { Upload, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PreAuthImportSectionProps {
  onFileSelect: (file: File) => void;
  onDownloadTemplate?: () => void;
  isProcessing?: boolean;
}

export function PreAuthImportSection({
  onFileSelect,
  onDownloadTemplate,
  isProcessing = false,
}: PreAuthImportSectionProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();

  const handleOpenFileDialog = useCallback(() => {
    if (isProcessing) return;
    inputRef.current?.click();
  }, [isProcessing]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isProcessing) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpenFileDialog();
      }
    },
    [isProcessing, handleOpenFileDialog],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
      event.target.value = '';
    },
    [onFileSelect],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (isProcessing) return;

      const file = event.dataTransfer.files?.[0];
      if (file) {
        onFileSelect(file);
      }
    },
    [isProcessing, onFileSelect],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (isProcessing) return;
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [isProcessing],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
    },
    [],
  );

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">
          See your instruments in Scopax
        </h2>
        <p className="text-muted-foreground">
          Drop your CSV or Excel file to preview how Scopax organizes your calibration data
        </p>
      </div>

      <div
        className={cn(
          'flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed p-8 transition-all cursor-pointer',
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-border/70 hover:border-primary/60 hover:bg-muted/30',
          isProcessing && 'cursor-not-allowed opacity-60',
        )}
        role="button"
        aria-disabled={isProcessing}
        aria-label="Upload instrument file"
        tabIndex={isProcessing ? -1 : 0}
        onClick={handleOpenFileDialog}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          {isProcessing ? (
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          ) : (
            <Upload className="h-8 w-8 text-primary" />
          )}
        </div>

        <div className="text-center space-y-1">
          <p className="text-base font-medium">
            {isProcessing ? 'Processing your file...' : 'Drop your CSV or Excel file here'}
          </p>
          <p className="text-sm text-muted-foreground">
            or click to browse
          </p>
        </div>

        {!isProcessing && (
          <Button variant="outline" size="sm" type="button" asChild>
            <label
              htmlFor={inputId}
              className="cursor-pointer"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleOpenFileDialog();
              }}
            >
              Browse files
            </label>
          </Button>
        )}

        <p className="text-xs text-muted-foreground">
          Your data stays in your browser until you sign up
        </p>
      </div>

      <input
        type="file"
        ref={inputRef}
        accept=".csv,.xls,.xlsx"
        className="hidden"
        id={inputId}
        onClick={(event) => event.stopPropagation()}
        onChange={handleFileChange}
        disabled={isProcessing}
      />

      <div className="flex items-center justify-between text-xs text-muted-foreground px-2">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" />
          <span>Supported: CSV, XLS, XLSX</span>
        </div>
        {onDownloadTemplate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDownloadTemplate();
            }}
            className="flex items-center gap-1 hover:text-foreground underline-offset-4 hover:underline"
          >
            <Download className="h-3 w-3" />
            Download sample template
          </button>
        )}
      </div>
    </div>
  );
}

export default PreAuthImportSection;
